import { INotification } from '../store/notifications/types';

const DB_NAME = 'AlertManagerDB';
const DB_VERSION = 1;
const STORE_NAME = 'notifications';

// Initialize IndexedDB
const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request: IDBOpenDBRequest = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error('Failed to open IndexedDB'));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, { keyPath: ['userId', 'id'] });
        objectStore.createIndex('userId', 'userId', { unique: false });
        objectStore.createIndex('dateTime', 'dateTime', { unique: false });
      }
    };
  });
};

// Get notifications for a specific user
export const getNotificationsFromIndexedDB = async (userId: string | null): Promise<INotification[]> => {
  if (!userId) return [];

  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('userId');
      const request = index.getAll(userId);

      request.onsuccess = () => {
        const notifications = request.result as INotification[];
        // Sort by id (timestamp) descending to get newest first
        notifications.sort((a, b) => b.id - a.id);
        resolve(notifications);
      };

      request.onerror = () => {
        reject(new Error('Failed to get notifications from IndexedDB'));
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('Error getting notifications from IndexedDB:', error);
    return [];
  }
};

// Save notifications for a specific user
export const saveNotificationsToIndexedDB = async (
  notifications: INotification[],
  userId: string | null,
): Promise<void> => {
  if (!userId) return;

  try {
    // Create plain objects from Redux proxy objects to avoid "revoked proxy" errors
    const plainNotifications = JSON.parse(JSON.stringify(notifications));

    const db = await initDB();

    // First, delete all existing notifications for this user
    await deleteAllNotificationsForUser(userId);

    // Then add new notifications
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      let completed = 0;
      let hasError = false;

      if (plainNotifications.length === 0) {
        transaction.oncomplete = () => {
          db.close();
          resolve();
        };
        return;
      }

      plainNotifications.forEach((notification: INotification) => {
        const request = store.add({ ...notification, userId });

        request.onsuccess = () => {
          completed++;
          if (completed === plainNotifications.length && !hasError) {
            transaction.oncomplete = () => {
              db.close();
              resolve();
            };
          }
        };

        request.onerror = () => {
          hasError = true;
          reject(new Error('Failed to add notification to IndexedDB'));
        };
      });

      transaction.onerror = () => {
        hasError = true;
        reject(new Error('Failed to save notifications to IndexedDB'));
      };
    });
  } catch (error) {
    console.error('Error saving notifications to IndexedDB:', error);
    throw error;
  }
};

// Delete all notifications by chatId for a specific user
export const deleteNotificationsByChatIdFromIndexedDB = async (
  chatId: number,
  userId: string | null,
): Promise<void> => {
  if (!userId) return;

  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('userId');
      const request = index.openCursor(IDBKeyRange.only(userId));

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue | null>).result;
        if (cursor) {
          const notification = cursor.value as INotification;
          if (notification.chatId === chatId) {
            cursor.delete();
          }
          cursor.continue();
        } else {
          // All deletions completed
          transaction.oncomplete = () => {
            db.close();
            resolve();
          };
        }
      };

      request.onerror = () => {
        reject(new Error('Failed to delete notifications by chatId from IndexedDB'));
      };

      transaction.onerror = () => {
        reject(new Error('Transaction failed while deleting notifications by chatId'));
      };
    });
  } catch (error) {
    console.error('Error deleting notifications by chatId from IndexedDB:', error);
    throw error;
  }
};

// Delete a specific notification by id
export const deleteNotificationByIdFromIndexedDB = async (
  notificationId: number,
  userId: string | null,
): Promise<void> => {
  if (!userId) return;

  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete([userId, notificationId]);

      request.onsuccess = () => {
        // Wait for transaction to complete before resolving
        transaction.oncomplete = () => {
          db.close();
          resolve();
        };
      };

      request.onerror = () => {
        reject(new Error('Failed to delete notification from IndexedDB'));
      };

      transaction.onerror = () => {
        reject(new Error('Transaction failed while deleting notification'));
      };
    });
  } catch (error) {
    console.error('Error deleting notification from IndexedDB:', error);
    throw error;
  }
};

// Delete all notifications for a specific user
export const deleteAllNotificationsForUser = async (userId: string | null): Promise<void> => {
  if (!userId) return;

  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('userId');
      const request = index.openCursor(IDBKeyRange.only(userId));

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursor | null>).result;
        if (cursor) {
          // Use cursor.delete() directly - it's safer than accessing primaryKey
          cursor.delete();
          cursor.continue();
        } else {
          // All deletions completed
          transaction.oncomplete = () => {
            db.close();
            resolve();
          };
        }
      };

      request.onerror = () => {
        reject(new Error('Failed to delete notifications from IndexedDB'));
      };

      transaction.onerror = () => {
        reject(new Error('Transaction failed while deleting notifications'));
      };
    });
  } catch (error) {
    console.error('Error deleting notifications from IndexedDB:', error);
    throw error;
  }
};

// Check if IndexedDB is supported
export const isIndexedDBSupported = (): boolean => {
  return typeof indexedDB !== 'undefined';
};
